import { useState, useEffect } from 'react';
import type { SelectSingleEventHandler } from "react-day-picker";

// Types
import { Event } from '../../types/event/event';
import { CalendarTileProperties } from '../../types/event/calendar';

// Services
import { fetchOrganizationEvents } from '@/services/event/organizationEvents';

export const useCalendarEvents = (tenantId: string | null) => {
    const [date, setDate] = useState<Date>(new Date());
    const [events, setEvents] = useState<Event[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let ignore = false;

        const getEvents = async () => {
            try {
                if (typeof tenantId !== "string") {
                    setError('No organization selected');
                    setLoading(false);
                    return;
                }
                const data = await fetchOrganizationEvents(tenantId);
                if (!ignore) {
                    setEvents(data);
                }
            } catch (error: unknown) {
                console.error('Error fetching events:', error);
                setError(error instanceof Error ? error.message : 'An error occurred');
            } finally {
                if (!ignore) {
                    setLoading(false);
                }
            }
        };

        getEvents();

        return () => {
            ignore = true;
        };
    }, [tenantId]);

    const onDateChange: SelectSingleEventHandler = (day) => {
        if (day) {
            setDate(day);
        }
    };

    const tileClassName = ({ date, view }: CalendarTileProperties): string | null => {
        if (view === 'month') {
            const day = date.getDay();
            if (day === 0 || day === 6) {
                const event = events.find(event => {
                    const eventDate = event.date.split('T')[0];
                    const tileDate = date.toISOString().split('T')[0];
                    return eventDate === tileDate;
                });
                if (event) {
                    return 'react-calendar__tile--has-event react-calendar__tile--weekend';
                }
                return 'react-calendar__tile--weekend';
            }
            const event = events.find(event => {
                const eventDate = event.date.split('T')[0];
                const tileDate = date.toISOString().split('T')[0];
                return eventDate === tileDate;
            });
            if (event) {
                return 'react-calendar__tile--has-event';
            }
        }
        return null;
    };

    const getEventsOnDate = (clickedDate: Date): Event[] => {
        return events.filter(event => {
            const eventDate = event.date.split('T')[0];
            const tileDate = clickedDate.toISOString().split('T')[0];
            return eventDate === tileDate;
        });
    };

    return {
        date,
        events,
        loading,
        error,
        onDateChange,
        tileClassName,
        getEventsOnDate
    };
}; 